import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { FormGroup, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { EMPTY, Observable, of } from "rxjs";
import { catchError, finalize, map } from "rxjs/operators";
import { AutomapperService } from "../../core/automapper/automapper.service";
import { BASE_API_URL } from "../../core/environment.tokens";
import { MessagingService } from "../../core/messaging/messaging.service";
import { SeverityType } from "../../core/messaging/severity-type.enum";
import { FormService } from "../../dynamic-forms/form.service";
import { CheckboxGroup } from "../../dynamic-forms/inputs/checkbox-group/checkbox-group.model";
import { DynamicInput } from "../../dynamic-forms/inputs/dynamic-input.model";
import { Textbox } from "../../dynamic-forms/inputs/textbox/textbox.model";
import { ChaseInvoiceStatusChangeRequest } from "../../platform/modules/approval-center/invoices/chase-invoice-status-change-request.model";
import { InvoicesService } from "../../platform/modules/invoice/invoices/invoices.service";
import { ChaseAmountDetail } from "../../platform/modules/invoice/invoices/model/chase-amount-detail.model";
import { VendorDetailService } from "../../platform/modules/invoice/vendor-detail/vendor-detail.service";
import { AddressDetailInfoService } from "../../platform/modules/retrieval/psr/address-detail/address-detail-info/address-detail-info.service";
import { RetrievalPageService } from "../../platform/modules/retrieval/retrieval-page/retrieval-page.service";
import { ArrayHelper } from "../../utilities/contracts/array-helper";
import { DateFormats } from "../../utilities/contracts/helper-types";
import { RegExHelper } from "../../utilities/reg-Ex-Helper";
import { BulkAction } from "../grid/bulk-actions/bulk-action.model";
import { GridPipeName } from "../grid/grid-pipe.enum";
import { GridColumnDefinition } from "../grid/models/grid-column-definition.model";
import { GridConfiguration } from "../grid/models/grid-configuration.model";
import { GridFilter } from "../grid/models/grid-filter.model";
import { GridRequest } from "../grid/models/grid-request.model";
import { InvoiceSettingService } from "../invoice/invoice-setting.service";

@Component({
  selector: "app-invoice-grid",
  templateUrl: "./invoice-grid.component.html",
  styleUrls: ["./invoice-grid.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})

export class InvoiceGridComponent implements OnInit {
  @ViewChild("chaseIdColumn", { static: true }) chaseIdColumn: TemplateRef<any>;
  @Input() url = `${this.baseApiUrl}invoice/chases`;
  @Input() additionalColumns: GridColumnDefinition[] = [];
  @Input() additionalFilters: GridFilter[] = [];
  bulkActions: BulkAction[] = [];
  @Input() selectionMode = "";
  @Input() showActionColumn = true;
  @Input() gridRequestType: string;
  chasesInvoiceGridSelection: any[];
  gridFilters: GridFilter[];
  invoiceGridConfiguration = new GridConfiguration();
  invoiceGridRequest: GridRequest;
  invoiceGridSelection: any[];
  selectedChases: any[];
  isConfirmationModalVisible = false;
  currentAction: string;
  refreshGrid = new EventEmitter<boolean>(true);
  confirmationModalText: string;
  confirmationModalHeader: string;
  invoiceDownloadForm: FormGroup;
  private pZipFileName: Textbox;
  showZipFileNameModal: boolean;
  selectedInvoiceIds: any[];
  isBulkActionLoading = false;
  private totalMatchingRecords = 0;
  private readonly maxBulkSelectionForDownload = 100;
  private readonly maxBulkSelectionForApproveDeny = 5000;
  private wasSelectionCapped = false;
  private totalSelectedBeforeCap = 0;
  constructor(@Inject(BASE_API_URL) private readonly baseApiUrl: string,
              private readonly automapper: AutomapperService,
              private readonly formService: FormService,
              private readonly retrievalPageService: RetrievalPageService,
              private readonly vendorDetailService: VendorDetailService,
              private readonly invoiceSettingService: InvoiceSettingService,
              private readonly invoicesService: InvoicesService,
              private readonly addressDetailInfoService: AddressDetailInfoService,
              private readonly messagingService: MessagingService,
              private router: Router, private readonly changeDetector: ChangeDetectorRef) {
  }

  ngOnInit(): void {
    this.createForm();
    this.getAllSelectableInputs();
    this.createGrid();
    this.setBulkActions();
  }

  get invoiceIdInput(): Textbox {
    return this.getInput("invoiceId") as Textbox;
  }

  get invoiceNumberInput(): Textbox {
    return this.getInput("invoiceNumber") as Textbox;
  }

  get chaseIdInput(): Textbox {
    return this.getInput("chaseId") as Textbox;
  }

  get projectIdsAsCsvInput(): CheckboxGroup {
    return this.getInput("projectIdsAsCsv") as CheckboxGroup;
  }
  set projectIdsAsCsvInput(value: CheckboxGroup) {
    this.setInput("projectIdsAsCsv", value);
  }

  get addressIdInput(): Textbox {
    return this.getInput("addressId") as Textbox;
  }

  get vendorIdsAsCsvInput(): CheckboxGroup {
    return this.getInput("vendorIdsAsCsv") as CheckboxGroup;
  }
  set vendorIdsAsCsvInput(value: CheckboxGroup) {
    this.setInput("vendorIdsAsCsv", value);
  }

  get invoiceTypeIdsAsCsvInput(): CheckboxGroup {
    return this.getInput("invoiceTypeIdsAsCsv") as CheckboxGroup;
  }
  set invoiceTypeIdsAsCsvInput(value: CheckboxGroup) {
    this.setInput("invoiceTypeIdsAsCsv", value);
  }

  get paymentTypeIdsAsCsvInput(): CheckboxGroup {
    return this.getInput("paymentTypesIdsAsCsv") as CheckboxGroup;
  }
  set paymentTypeIdsAsCsvInput(value: CheckboxGroup) {
    this.setInput("paymentTypesIdsAsCsv", value);
  }

  get amountInput(): Textbox {
    return this.getInput("amount") as Textbox;
  }

  get statusIdsAsCsvInput(): CheckboxGroup {
    return this.getInput("statusIdsAsCsv") as CheckboxGroup;
  }
  set statusIdsAsCsvInput(value: CheckboxGroup) {
    this.setInput("statusIdsAsCsv", value);
  }

  getInput<T extends DynamicInput>(key: string): T {
    if (this.invoiceGridRequest == null) {
      return null;
    }

    return this.invoiceGridRequest.getInput<T>(key);
  }

  setInput<T extends DynamicInput>(key: string, value: T): void {
    if (this.invoiceGridRequest == null) {
      return null;
    }

    this.invoiceGridRequest.setInput<T>(key, value);
  }

  private getFilters(): GridFilter[] {
    const totalFilters = [
      ...this.additionalFilters,
      new GridFilter({
        input: new Textbox(),
        key: "invoiceId",
        name: "Invoice ID",
      }),
      new GridFilter({
        input: new Textbox(),
        key: "invoiceNumber",
        name: "Invoice Number",
      }),
      new GridFilter({
        input: new Textbox(),
        key: "chaseId",
        name: "Chase ID",
      }),
      new GridFilter({
        input: new CheckboxGroup(),
        key: "projectIdsAsCsv",
        name: "Projects",
      }),
      new GridFilter({
        input: new Textbox(),
        key: "addressId",
        name: "AID",
      }),
      new GridFilter({
        input: new CheckboxGroup(),
        key: "vendorIdsAsCsv",
        name: "Vendors",
      }),
      new GridFilter({
        input: new CheckboxGroup(),
        key: "invoiceTypeIdsAsCsv",
        name: "Invoice Type",
      }),
      new GridFilter({
        input: new CheckboxGroup(),
        key: "paymentTypesIdsAsCsv",
        name: "Payment Type",
      }),
      new GridFilter({
        input: new Textbox(),
        key: "amount",
        name: "Amount",
      }),
      new GridFilter({
        input: new CheckboxGroup(),
        key: "statusIdsAsCsv",
        name: "Status",
      }),
    ];

    const distinctFilters = totalFilters.filter((filter, i, filters) => {
      return filters.findIndex(a => a.key === filter.key) === i;
    });
    this.gridFilters = distinctFilters;
    return distinctFilters;
  }

  createGrid(): void {
    this.invoiceGridConfiguration.columns = [
      new GridColumnDefinition({ field: "invoiceId", header: "INVOICE ID", routeUrl: "/invoices/detail/:invoiceId/chase" }),
      new GridColumnDefinition({ field: "invoiceNumber", header: "INVOICE NUMBER" }),
      new GridColumnDefinition({ field: "chaseId", header: "CHASE ID", template: this.chaseIdColumn }),
      new GridColumnDefinition({ field: "projectName", header: "PROJECT" }),
      new GridColumnDefinition({ field: "masterDocumentSourceId", header: "AID" }),
      new GridColumnDefinition({ field: "vendorName", header: "VENDOR" }),
      new GridColumnDefinition({ field: "invoiceType", header: "TYPE" }),
      new GridColumnDefinition({ field: "chaseAmount", header: "AMOUNT PER CHASE", pipeName: GridPipeName.Currency }),
      new GridColumnDefinition({ field: "status", header: "STATUS" }),
    ];

    if (!this.isApprovalCenterGrid) {
      this.invoiceGridConfiguration.columns.push(new GridColumnDefinition({ field: "paymentType", header: "PAYMENT TYPE" }));
      this.invoiceGridConfiguration.columns.push(new GridColumnDefinition({ field: "paymentDate", header: "PAYMENT DATE", pipeName: GridPipeName.Date, format: DateFormats.GRID_DATE_FORMAT }));
      this.invoiceGridConfiguration.columns.push(new GridColumnDefinition({ field: "paidBy", header: "PAID BY" }));
    }

    this.invoiceGridConfiguration.pageSize = 10;
    this.invoiceGridConfiguration.showActionColumn = this.showActionColumn;
    this.invoiceGridConfiguration.selectionMode = this.selectionMode;
    this.invoiceGridConfiguration.stateName = "approveInvoices";
    this.invoiceGridConfiguration.trackByField = "chaseId";
    this.invoiceGridConfiguration.selectAllPages = true;
    this.invoiceGridConfiguration.autoSelectPagesWhenAllSelected = true;
    this.invoiceGridConfiguration.excludeDeselectedRowsWhenAllSelected = true;
    this.invoiceGridRequest = new GridRequest({
      url: this.url,
      filters: this.getFilters(),
    });

  }

  trackByIndex(index, item) {
    return index;
  }

  onGridDataLoaded(data: any[]): void {
    this.totalMatchingRecords = ArrayHelper.isAvailable(data) ? (data[0].recordCount || data.length) : 0;
  }

  private get selectedAcrossPagesCount(): number {
    const excludedCount = ArrayHelper.isAvailable(this.invoiceGridRequest.excludedKeys) ? this.invoiceGridRequest.excludedKeys.length : 0;
    return Math.max(this.totalMatchingRecords - excludedCount, 0);
  }

  private setBulkActionsDisabled(disabled: boolean): void {
    this.bulkActions.forEach(action => action.disabled = disabled);
  }

  private getAllSelectableInputs(): void {
    this.getProjects();
    this.getVendors();
    this.getInvoiceTypes();
    this.getPaymentTypes();
    this.getInvoiceStatuses();
  }

  getProjects(): void {
    this.retrievalPageService
      .getProjectList()
      .pipe(map(this.automapper.curryMany("LookupModel", "SelectableInput")))
      .subscribe(result => {
        this.projectIdsAsCsvInput = { ...this.projectIdsAsCsvInput, options: result } as any;
        this.formService.updateDom.next();
      });
  }

  getVendors(): void {
    this.addressDetailInfoService
      .getVendors()
      .pipe(map(this.automapper.curryMany("Vendors", "SelectableInput")))
      .subscribe(options => {
        this.vendorIdsAsCsvInput = { ...this.vendorIdsAsCsvInput, options } as any;
        this.formService.updateDom.next();
      });
  }

  getPaymentTypes(): void {
    this.vendorDetailService
      .getPaymentMethods()
      .pipe(map(this.automapper.curryMany("ListItem", "SelectableInput")))
      .subscribe(result => {
        this.paymentTypeIdsAsCsvInput = { ...this.paymentTypeIdsAsCsvInput, options: result } as any;
        this.formService.updateDom.next();
      });
  }

  getInvoiceTypes(): void {
    this.invoiceSettingService
      .getInvoiceTypes()
      .pipe(map(this.automapper.curryMany("default", "SelectableInput")))
      .subscribe(result => {
        this.invoiceTypeIdsAsCsvInput = { ...this.invoiceTypeIdsAsCsvInput, options: result } as any;
        this.formService.updateDom.next();
      });
  }

  getInvoiceStatuses(): void {
    this.invoicesService.getInvoiceStatuses()
      .pipe(map(this.automapper.curryMany("ListItem", "SelectableInput")))
      .subscribe(result => {
        this.statusIdsAsCsvInput = { ...this.statusIdsAsCsvInput, options: result } as any;
        this.formService.updateDom.next();
      });
  }

  get isApprovalCenterGrid(): boolean {
    return this.gridRequestType === "InvoiceApprovalCenter";
  }

  approveRequestItem = (rowData: any): void => {
    this.setSelectedChases(rowData, () => {
      this.currentAction = "Approved";
      this.confirmationModalHeader = "APPROVE";
      this.confirmationModalText = this.wasSelectionCapped
        ? `You have selected ${this.totalSelectedBeforeCap} invoices to approve, but only the first ${this.maxBulkSelectionForApproveDeny.toLocaleString()} will be approved.\nThis will change the status to "Ready For Payment".\nAre you sure you want to continue?`
        : "This will change the status to \"Ready For Payment\".\nAre you sure you want to approve the invoice(s)?";
      this.isConfirmationModalVisible = true;
      this.changeDetector.markForCheck();
    });
  }

  denyRequestItem = (rowData: any): void => {
    this.setSelectedChases(rowData, () => {
      this.currentAction = "Denied";
      this.confirmationModalHeader = "DENY";
      this.confirmationModalText = this.wasSelectionCapped
        ? `You have selected ${this.totalSelectedBeforeCap} invoices to deny, but only the first ${this.maxBulkSelectionForApproveDeny.toLocaleString()} will be denied.\nThis will create Pend PC1008: Invoice Above Threshold/Client Denied.\nAre you sure you want to continue?`
        : "This will create Pend PC1008: Invoice Above Threshold/Client Denied.\nAre you sure you want to deny the invoice(s)?";
      this.isConfirmationModalVisible = true;
      this.changeDetector.markForCheck();
    });
  }

  private setSelectedChases(rowData: any, callback: () => void): void {
    const isBulkAcrossAllPages = ArrayHelper.isAvailable(rowData) && this.invoiceGridRequest.isAllSelected;
    this.totalSelectedBeforeCap = isBulkAcrossAllPages ? this.selectedAcrossPagesCount : (ArrayHelper.isAvailable(rowData) ? rowData.length : 1);
    this.wasSelectionCapped = this.totalSelectedBeforeCap > this.maxBulkSelectionForApproveDeny;
    this.getSelectedChases(rowData).subscribe(chases => {
      this.selectedChases = chases;
      callback();
    });
  }

  private getSelectedChases(rowData: any, maxSelection?: number): Observable<any[]> {
    const isBulkInvocation = ArrayHelper.isAvailable(rowData);
    if (isBulkInvocation && this.invoiceGridRequest.isAllSelected) {
      if (maxSelection != null && this.selectedAcrossPagesCount > maxSelection) {
        this.messagingService.showToast(
          `You can select a maximum of ${maxSelection} requests. Please reduce your selection to proceed.`,
          SeverityType.WARN);
        return EMPTY;
      }
      this.isBulkActionLoading = true;
      this.setBulkActionsDisabled(true);
      return this.invoicesService.getAllMatchingChases(this.invoiceGridRequest, maxSelection ?? this.maxBulkSelectionForApproveDeny)
        .pipe(
          map(chases => this.excludeDeselectedChases(chases)),
          catchError(() => {
            this.messagingService.showToast("Unable to load the selected requests. Please try again.", SeverityType.ERROR);
            return EMPTY;
          }),
          finalize(() => {
            this.isBulkActionLoading = false;
            this.setBulkActionsDisabled(false);
            this.changeDetector.markForCheck();
          }));
    }
    return of(isBulkInvocation ? rowData : [rowData]);
  }

  private excludeDeselectedChases(chases: any[]): any[] {
    if (!ArrayHelper.isAvailable(this.invoiceGridRequest.excludedKeys)) {
      return chases;
    }
    return chases.filter(chase => !this.invoiceGridRequest.excludedKeys.includes(chase.chaseId));
  }

  continueAction() {
    this.isConfirmationModalVisible = false;
    this.changeChaseInvoiceStatus(this.currentAction);
  }

  cancelAction() {
    this.isConfirmationModalVisible = false;
    this.clearGridSelection();
  }

  private clearGridSelection(): void {
    this.invoiceGridSelection = [];
    this.selectedChases = [];
    this.invoiceGridRequest.isAllSelected = false;
    this.invoiceGridRequest.excludedKeys = [];
  }

  private changeChaseInvoiceStatus(status: string) {
    const chaseAmountDetails = this.getChaseAmountDetails();
    const chaseInvoiceStatusChangeRequest = new ChaseInvoiceStatusChangeRequest({ chaseAmountDetails, status });
    this.isBulkActionLoading = true;
    this.setBulkActionsDisabled(true);
    this.invoicesService.updateInvoiceChaseStatus(chaseInvoiceStatusChangeRequest)
      .pipe(
        catchError(() => {
          this.messagingService.showToast(`Unable to ${status.toLowerCase()} the chase invoice(s). Please try again.`, SeverityType.ERROR);
          return EMPTY;
        }),
        finalize(() => {
          this.isBulkActionLoading = false;
          this.setBulkActionsDisabled(false);
          this.changeDetector.markForCheck();
        }))
      .subscribe(() => {
        this.messagingService.showToast(`Chase Invoice ${status} successfully.`, SeverityType.SUCCESS);
        this.refreshGrid.emit();
        this.currentAction = "";
        this.clearGridSelection();
      });
  }

  private setBulkActions(): void {
    if (this.isApprovalCenterGrid) {
      this.bulkActions = [
        new BulkAction({
          name: "Approve",
          action: this.approveRequestItem,
          showBulkAction: true,
        }),
        new BulkAction({
          name: "Deny",
          action: this.denyRequestItem,
          showBulkAction: true,
        }),
        new BulkAction({
          name: "Edit",
          action: this.editRequestItem,
          showBulkAction: false,
        }),
        new BulkAction({
          name: "Download Invoice",
          action: this.onDownloadInvoice.bind(this),
        }),
      ];
    }
  }
  setZipFileName(value: Textbox): void {
    this.pZipFileName = new Textbox({ ...this.getZipFileName(), ...value } as any);
    if (this.invoiceDownloadForm != null) {
      this.invoiceDownloadForm.get(this.getZipFileName().key).setValue(this.getZipFileName().value);
    }
  }

  private createForm(): void {
    this.setZipFileName({
      key: "zipfilename",
      label: "Zip File Name",
      validators: [
        Validators.required,
        Validators.minLength(5),
        Validators.maxLength(32),
        Validators.pattern(RegExHelper.fileNamePattern),
      ],
      errorMessages: {
        required: "Zip File Name is required",
        minlength: "Zip File Name must be between 5 and 32 characters in length",
        maxlength: "Zip File Name must be between 5 and 32 characters in length",
        pattern: "Valid characters for a file name are 0-9 a-z A-Z . _ - & ",
      },
    } as any);
    this.invoiceDownloadForm = this.formService.createFormGroup([
      this.getZipFileName(),
    ]);
  }
  zipFileNameChanged(): void {
    if (this.invoiceDownloadForm.valid) {
      const zipFileName = this.invoiceDownloadForm.value[this.getZipFileName().key];
      if (this.isApprovalCenterGrid) {
        this.selectedInvoiceIds.map(x => { x.isInvoiceDownloadFromApprovalCenter = true; });
      }
      this.invoicesService.downloadZippedInvoices(this.selectedInvoiceIds, encodeURIComponent(zipFileName))
        .subscribe((result: boolean) => {
          if (result) {
            this.messagingService.showToast("Invoices Download Submission Started", SeverityType.SUCCESS);

          } else {
            this.messagingService.showToast("Invoices Download Submission Failed", SeverityType.ERROR);
          }
          this.clearInvoiceGridSelection();
        });

      this.showZipFileNameModal = false;
    }
  }
  getZipFileName(): Textbox {
    return this.pZipFileName;
  }
  onDownloadInvoice(rowData: any): void {
    this.selectedInvoiceIds = [];
    this.getSelectedChases(rowData, this.maxBulkSelectionForDownload).subscribe(selectedInvoices => {
      this.selectedInvoiceIds = selectedInvoices.filter(invoice => invoice.invoiceId);
      this.invoiceDownloadForm.get(this.getZipFileName().key).setValue("");
      this.showZipFileNameModal = true;
      this.changeDetector.markForCheck();
    });
  }
  private getChaseAmountDetails(): ChaseAmountDetail[] {
    const chaseAmountDetails = [];
    this.selectedChases.forEach(chase => {
      chaseAmountDetails.push(new ChaseAmountDetail({ chaseId: chase.chaseId, amountPerChase: chase.chaseAmount }));
    });
    return chaseAmountDetails;
  }

  editRequestItem = (rowData: any): void => {
    const invoiceId = rowData.invoiceId;
    this.router.navigate([`/invoices/detail/${invoiceId}/chase`]);
  }

  isEditDisabled(invoiceType?: string, actionName?: string): boolean {
    return actionName === "Edit" ? invoiceType === "NET30" : false;
  }
  clearInvoiceGridSelection(): void {
    this.invoiceGridSelection = [];
    this.selectedInvoiceIds = [];
    this.invoiceGridRequest.isAllSelected = false;
    this.invoiceGridRequest.excludedKeys = [];
    this.changeDetector.markForCheck();
  }
}
